import { AsyncPipe } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TuiButton, TuiHint, TuiSizeL, TuiSizeXS } from '@taiga-ui/core';
import { TuiButtonLoading } from '@taiga-ui/kit';
import { tap } from 'rxjs';
import { ExportStateService } from '../../services';

@Component({
	standalone: true,
	imports: [TuiButton, TuiButtonLoading, TranslocoPipe, AsyncPipe, TuiHint],
	selector: 'mar-export-button',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: `
		@if ({ export: generating$ | async }; as state) {
			<!-- TODO: (Taiga UI migration) [appearance] binding uses a dynamic expression. If it can produce "error"/"success"/"glass", replace with "negative"/"positive"/"secondary-grayscale" -->
			<button
				tuiButton
				[size]="size()"
				[appearance]="appearance()"
				(click)="onExport()"
				[disabled]="isDisabled()"
				[loading]="loading()"
				[tuiHint]="hint()"
			>
				{{ label() | transloco }}
			</button>
		}
	`,
})
export class ExportButtonComponent {
	readonly label = input<string>('action.export');
	readonly appearance = input<string>('');
	readonly size = input<TuiSizeXS | TuiSizeL>('m');
	readonly disabled = input<boolean>(false);

	readonly export = output<void>();

	readonly hasActiveExport = signal<boolean>(false);
	readonly loading = signal<boolean>(false);

	readonly exportStateService = inject(ExportStateService);
	readonly transloco = inject(TranslocoService);

	readonly generating$ = this.exportStateService.generating$.pipe(
		tap((value) => {
			this.hasActiveExport.set(!!value);
			this.loading.set((value && !value.endedAt) ?? false);
		}),
	);

	readonly isDisabled = computed(() => {
		if (this.disabled()) return true;
		if (this.hasActiveExport()) return true;
		return false;
	});

	readonly hint = computed(() => {
		if (!this.hasActiveExport()) return null;
		return this.transloco.translate('export.active-warning');
	});

	onExport() {
		this.export.emit();
	}
}
